import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Award,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  DollarSign,
  FileJson,
  Flame,
  Loader2,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import BarChart from "@/components/RefClick/BarChart";
import { statsApi } from "@/lib/stats-api";
import { downloadFromUrl } from "@/lib/download";
import { monogramStyle, monogramText } from "@/lib/refclick-utils";
import EarningsStatsSection from "@/components/RefClick/EarningsStatsSection";

// Format YYYY-MM into "September 2026" — locale-neutral month name.
function labelForMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return ym;
  const d = new Date(Date.UTC(y, m - 1, 15));
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  }).format(d);
}

// Advance a YYYY-MM string by +/-1 month with wrap-around.
function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const base = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${base.getUTCFullYear()}-${String(base.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * Full-page Stats view (route: /stats).
 *
 * Sections:
 *   1. Header — back button + wordmark + export actions
 *   2. Top row — three headline cards: current streak · best streak ·
 *      this month total. Each is a compact glass tile with an icon,
 *      Doto counter, and a short caption.
 *   3. 30-day chart — animated bars, today highlighted, hover tooltip.
 *   4. Per-link breakdown — one row per link: monogram, name, 30-day
 *      total, target-hit days, completion-rate bar.
 *
 * Layout is responsive: cards stack on phones, chart shrinks height.
 */
export default function Stats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null); // 'csv' | 'json' | null
  // Month browser state — the "current" month is filled in once we know
  // what the server thinks today's Dhaka month is. Starts as null so we
  // don't render an out-of-sync label on the first paint.
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [monthLoading, setMonthLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await statsApi.stats();
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load stats");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load whichever month the browser is pointed at. On mount `selectedMonth`
  // is null so the server picks "current Dhaka month" — its response tells
  // us which month that is, and we lock the state to it so the arrows work.
  useEffect(() => {
    let cancelled = false;
    setMonthLoading(true);
    statsApi
      .statsMonthly(selectedMonth || undefined)
      .then((d) => {
        if (cancelled) return;
        setMonthData(d);
        if (!selectedMonth) setSelectedMonth(d.month);
      })
      .catch(() => {
        /* keep the previous month view; a toast would be noisy */
      })
      .finally(() => {
        if (!cancelled) setMonthLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMonth]);

  const daily = data?.daily_totals_30d || [];

  // Prev/next arrow navigation. Prev is enabled as long as an earlier
  // month exists in `available_months`; next is disabled beyond the
  // current Dhaka month (the server marks `is_current` for us).
  const availableMonths = monthData?.available_months || [];
  const earliestMonth = availableMonths[0];
  const canGoPrev = !!selectedMonth && !!earliestMonth && selectedMonth > earliestMonth;
  const canGoNext = monthData ? !monthData.is_current : false;

  const goPrev = () => {
    if (!canGoPrev || !selectedMonth) return;
    setSelectedMonth(shiftMonth(selectedMonth, -1));
  };
  const goNext = () => {
    if (!canGoNext || !selectedMonth) return;
    setSelectedMonth(shiftMonth(selectedMonth, +1));
  };
  const goCurrent = () => {
    if (!monthData || monthData.is_current) return;
    // Move to whichever month the server considers current.
    setSelectedMonth(null);
  };

  const monthLabel = selectedMonth ? labelForMonth(selectedMonth) : "…";

  const handleExport = async (kind) => {
    setDownloading(kind);
    try {
      const url = kind === "csv" ? statsApi.exportCsvUrl() : statsApi.exportJsonUrl();
      const stamp = new Date().toISOString().slice(0, 10);
      const filename =
        kind === "csv"
          ? `refclick-export-${stamp}.csv`
          : `refclick-backup-${stamp}.json`;
      await downloadFromUrl(url, filename);
      toast.success(`Downloaded ${filename}`);
    } catch {
      toast.error(`Export failed — try again?`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div
      className="ambient-bg relative min-h-screen w-full text-text-main"
      data-testid="stats-view"
    >
      <main className="relative z-10 mx-auto flex w-full max-w-4xl flex-col px-2 py-4 sm:px-6 sm:py-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="glass-panel overflow-hidden rounded-2xl"
        >
          {/* Header */}
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 px-4 py-4 sm:px-6 sm:py-5">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                data-testid="stats-back-btn"
                className="focus-accent inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-text-muted transition-colors hover:border-accent/30 hover:text-accent"
                aria-label="Back to dashboard"
              >
                <ArrowLeft className="h-4 w-4" />
              </Link>
              <div className="flex flex-col leading-tight">
                <span
                  className="font-display text-lg font-bold tracking-tight sm:text-2xl"
                  data-testid="stats-title"
                >
                  Stats
                </span>
                <span className="hidden text-[11px] uppercase tracking-[0.18em] text-text-muted/80 sm:block">
                  Last 30 days · Dhaka
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2" data-testid="stats-actions">
              <Button
                type="button"
                onClick={() => handleExport("csv")}
                disabled={!!downloading}
                data-testid="export-csv-btn"
                className="h-9 gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 text-text-muted hover:border-accent/40 hover:text-accent"
              >
                {downloading === "csv" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                <span className="hidden sm:inline">CSV</span>
              </Button>
              <Button
                type="button"
                onClick={() => handleExport("json")}
                disabled={!!downloading}
                data-testid="export-json-btn"
                className="h-9 gap-1.5 rounded-lg border border-accent/40 bg-accent/15 px-3 text-accent hover:bg-accent/25"
              >
                {downloading === "json" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileJson className="h-3.5 w-3.5" />
                )}
                <span className="hidden sm:inline">JSON</span>
              </Button>
            </div>
          </header>

          {/* Content */}
          {loading ? (
            <div
              className="flex items-center justify-center gap-2 py-16 text-sm text-text-muted"
              data-testid="stats-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> Crunching…
            </div>
          ) : error ? (
            <div
              className="px-6 py-16 text-center font-mono-safe text-sm text-red-300"
              data-testid="stats-error"
            >
              {error}
              <div className="mt-3">
                <Button
                  onClick={() => navigate(0)}
                  className="border border-white/10 bg-white/[0.03] hover:border-accent/40 hover:text-accent"
                >
                  Try again
                </Button>
              </div>
            </div>
          ) : (
            <div className="px-3 pb-6 pt-4 sm:px-6 sm:pb-8">
              {/* Headline cards — streaks stay all-time (they're
                  identity metrics), the third card shows clicks in
                  the SELECTED month so it stays in sync with the
                  month browser below. */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <HeadlineCard
                  icon={<Flame className="h-4 w-4" />}
                  label="Current streak"
                  value={data.current_streak}
                  suffix={data.current_streak === 1 ? "day" : "days"}
                  testid="stat-current-streak"
                  tone="accent"
                />
                <HeadlineCard
                  icon={<Award className="h-4 w-4" />}
                  label="Best streak"
                  value={data.best_streak}
                  suffix={data.best_streak === 1 ? "day" : "days"}
                  testid="stat-best-streak"
                  tone="muted"
                />
                <HeadlineCard
                  icon={<CalendarDays className="h-4 w-4" />}
                  label={monthLabel}
                  value={monthData?.total_clicks ?? 0}
                  suffix={
                    (monthData?.total_clicks ?? 0) === 1 ? "click" : "clicks"
                  }
                  testid="stat-month-total"
                  tone="muted"
                />
              </div>

              {/* Month browser — prev/next arrows around the month
                  label. `is_current` from the server determines when
                  the next arrow disables; `available_months` bounds
                  the prev arrow so we can't wander into empty history. */}
              <section
                className="mt-6 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-3 sm:px-5 sm:py-4"
                data-testid="month-browser-section"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={goPrev}
                      disabled={!canGoPrev || monthLoading}
                      data-testid="month-prev-btn"
                      aria-label="Previous month"
                      className="focus-accent inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-text-muted transition-colors hover:border-accent/40 hover:text-accent disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <div
                      className="min-w-[10rem] text-center"
                      data-testid="month-browser-label"
                      data-month={selectedMonth || ""}
                    >
                      <div className="font-display text-base font-bold tracking-tight sm:text-lg">
                        {monthLabel}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted/70">
                        {monthData?.is_current
                          ? "Current month · Dhaka"
                          : "Historical · Dhaka"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={goNext}
                      disabled={!canGoNext || monthLoading}
                      data-testid="month-next-btn"
                      aria-label="Next month"
                      className="focus-accent inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-text-muted transition-colors hover:border-accent/40 hover:text-accent disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    {!monthData?.is_current && (
                      <button
                        type="button"
                        onClick={goCurrent}
                        data-testid="month-current-btn"
                        className="focus-accent ml-1 inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-accent transition-colors hover:bg-accent/25"
                      >
                        Jump to current
                      </button>
                    )}
                  </div>

                  {/* Month-scoped totals */}
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-end leading-none">
                      <span className="text-[9px] uppercase tracking-[0.18em] text-text-muted/70">
                        Earnings
                      </span>
                      <span
                        className="font-display text-xl font-bold text-accent"
                        data-testid="month-total-usd"
                      >
                        ${(monthData?.total_usd ?? 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex flex-col items-end leading-none">
                      <span className="text-[9px] uppercase tracking-[0.18em] text-text-muted/70">
                        Clicks
                      </span>
                      <span
                        className="font-mono-num text-lg font-semibold text-text-main"
                        data-testid="month-total-clicks"
                      >
                        {monthData?.total_clicks ?? 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Per-link breakdown for the selected month */}
                <div
                  className="mt-4 flex flex-col gap-2"
                  data-testid="month-per-link"
                >
                  {monthLoading && !monthData ? (
                    <p className="rounded-lg border border-dashed border-white/10 py-4 text-center font-mono-safe text-[11px] text-text-muted">
                      Loading month…
                    </p>
                  ) : (() => {
                    const rows = Array.isArray(monthData?.per_link)
                      ? monthData.per_link
                      : [];
                    if (rows.length === 0) {
                      return (
                        <p
                          className="rounded-lg border border-dashed border-white/10 py-4 text-center font-mono-safe text-[11px] text-text-muted"
                          data-testid="month-per-link-empty"
                        >
                          No activity this month yet.
                        </p>
                      );
                    }
                    return rows.map((l) => (
                      <MonthLinkRow key={l.link_id} link={l} />
                    ));
                  })()}
                </div>
              </section>

              {/* Chart (ambient — last 30 days activity, independent
                  of the month selector) */}
              <section
                className="mt-6 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-4 sm:px-5"
                data-testid="stats-chart-section"
              >
                <div className="mb-3 flex items-baseline justify-between">
                  <h3 className="text-[11px] font-bold uppercase tracking-[0.22em] text-text-muted">
                    Last 30 days
                  </h3>
                  <span className="font-mono-safe text-[11px] text-text-muted">
                    total {daily.reduce((a, d) => a + (d.total || 0), 0)}
                  </span>
                </div>
                <BarChart data={daily} height={180} />
              </section>

              {/* Phase 3 — earnings summary */}
              <EarningsStatsSection />
            </div>
          )}
        </motion.div>

        <p
          className="mx-auto mt-5 max-w-full truncate text-center text-[11px] text-text-muted/60"
          data-testid="stats-footer-note"
        >
          Resets at midnight (Dhaka) · Month view resets on the 1st
        </p>
      </main>
    </div>
  );
}

function MonthLinkRow({ link }) {
  const daysBase = Math.max(1, link.days_elapsed || link.days_in_month || 1);
  const pct = Math.min(100, Math.round((link.target_hit_days / daysBase) * 100));
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-white/5 bg-white/[0.015] px-3 py-2.5"
      data-testid="month-link-row"
      data-link-id={link.link_id}
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-display text-sm font-bold text-white/95"
        style={monogramStyle(link.name)}
        aria-hidden
      >
        {monogramText(link.name)}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <span
            className="truncate text-sm font-semibold text-text-main"
            title={link.name}
          >
            {link.name}
          </span>
          <span
            className="ml-auto flex shrink-0 items-baseline gap-1.5 font-mono-num text-xs text-text-muted"
            data-testid="month-link-clicks"
          >
            <span className="text-text-main">{link.clicks}</span>
            <span className="text-text-muted/70">clicks</span>
          </span>
          <span
            className="inline-flex items-center gap-0.5 rounded-full border border-accent/25 bg-accent/10 px-1.5 py-[1px] font-mono-num text-[10.5px] font-semibold text-accent"
            data-testid="month-link-usd"
          >
            <DollarSign className="h-2.5 w-2.5" />
            {Number(link.total_usd || 0).toFixed(2)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
            <motion.div
              className="h-full rounded-full bg-accent"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              style={{ boxShadow: "0 0 8px rgb(var(--accent) / 0.5)" }}
            />
          </div>
          <span
            className="w-20 shrink-0 text-right font-mono-num text-[11px] text-text-muted"
            data-testid="month-link-hits"
          >
            {link.target_hit_days}/{daysBase} met
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-text-muted/70">
          <Target className="h-3 w-3" />
          <span>Daily target {link.daily_target}</span>
        </div>
      </div>
    </div>
  );
}

function HeadlineCard({ icon, label, value, suffix, testid, tone = "muted" }) {
  const accented = tone === "accent";
  return (
    <div
      className={
        "rounded-xl border px-3 py-3 sm:px-4 sm:py-4 " +
        (accented
          ? "border-accent/40 bg-accent/10"
          : "border-white/5 bg-white/[0.02]")
      }
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-text-muted">
        <span className={accented ? "text-accent" : "text-text-muted"}>
          {icon}
        </span>
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span
          className={
            "font-display text-2xl font-bold leading-none sm:text-3xl " +
            (accented ? "text-accent" : "text-text-main")
          }
        >
          {value}
        </span>
        <span className="font-mono-safe text-[11px] text-text-muted">
          {suffix}
        </span>
      </div>
    </div>
  );
}

function PerLinkRow_UNUSED() {
  // Legacy 30d-per-link row helper — kept but unused after the Stats
  // page pivoted to a per-month view. The month-scoped `MonthLinkRow`
  // above replaces it. Left here to avoid a large diff; safe to delete
  // in a follow-up cleanup.
  return null;
}
